import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import { ShiftTiming } from 'Interfaces/ReferenceInterfaces/ShiftTimingsInterfaces';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import classes from './shiftTimingsTable.module.scss';
import { useShiftTimingTable } from './useShiftTimingsTable';
import AddRowModalShiftTiming from './utils/AddRowModalShiftTimings';
import DeleteConfirmationModalShiftTiming from './utils/DeleteConfirmationModalShiftTimings';

interface ShiftTimingTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const ShiftTimingTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: ShiftTimingTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;
    const {
      shiftTimingData,
      isLoading,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData
    } = useShiftTimingTable(pageSize);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: ShiftTiming) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const formatTime = (timeString: string) => {
      if (!timeString) {
        return '';
      }
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`; // Returns just HH:MM (without seconds)
    };

    if (isLoading && !shiftTimingData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.shiftTimingTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Shift Timings Configuration</h2>
          {showAddButton && (
            <Button
              label="Add New Shift Timing"
              icon="pi pi-plus"
              className={classes.addButton}
              onClick={() => {
                setSelectedRow(null);
                setShowAddModal(true);
              }}
            />
          )}
        </div>

        <DataTable
          value={shiftTimingData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
        >
          <Column field="shift_name" header="Shift Name" />
          <Column
            field="start_time"
            header="Start Time"
            body={rowData => formatTime(rowData.start_time)}
          />
          <Column
            field="end_time"
            header="End Time"
            body={rowData => formatTime(rowData.end_time)}
          />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalShiftTiming
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
        />

        <DeleteConfirmationModalShiftTiming
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default ShiftTimingTable;
